// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdImage,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  getImageUrl,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import EmptyTable from '../../../common/EmptyTable';
import ChampionshipSponsorFields from '../../../fields/ChampionshipSponsorFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
type $Props = $Translate & {
  id: string;
  item: $Model<'Championship'>;
};

type $State = {
  data: Array<$Model<'ChampionshipSponsor'>>;
  loading: boolean;
};

class ChampionshipSponsorsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.ChampionshipId && data.modelName === 'ChampionshipSponsor') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const ChampionshipId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyChampionshipSponsor({
          ChampionshipId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    const ChampionshipId = item.id;

    const newItem: $ModelUpsertParams<'ChampionshipSponsor'> = {
      ChampionshipId,
      Image: '',
      Link: '',
      Name: '',
    };

    return (
      <SectionHeader
        bottomBorder
        iconClass={'icon-wallet'}
        rightButtons={(
          <FormDialog
            fields={(
              <ChampionshipSponsorFields item={newItem} />
            )}
            id={'create-championship-sponsor-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'ChampionshipSponsor'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertChampionshipSponsor(params);
              },
            })}
            subTitle={t('New')}
            title={t('Championship sponsor')}
          >
            <Button
              color={'info'}
              id={'create-championship-sponsor-button'}
              size={'sm'}
              title={t('Add new championship sponsor')}
            >
              {t('Add new')}
            </Button>
          </FormDialog>
        )}
        title={t('Championship sponsors')}
      />
    );
  }

  renderButtons(dataItem: $Model<'ChampionshipSponsor'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <ChampionshipSponsorFields item={dataItem} />
          )}
          id={`update-championship-sponsor-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'ChampionshipSponsor'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertChampionshipSponsor(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Championship sponsor')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-championship-sponsor-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-championship-sponsor-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteChampionshipSponsor(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Championship sponsor')}
        >
          <Button
            color={'danger'}
            id={`delete-championship-sponsor-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th />
            <th>{t('Name')}</th>
            <th>{t('Link')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'ChampionshipSponsor'>) => (
            <tr key={dataItem.id}>
              <TdImage
                largeImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'max',
                })}
                noImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: null,
                  size: 'min',
                })}
                smallImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'min',
                })}
                value={dataItem.Image}
              />
              <td>{dataItem.Name}</td>
              <td>{dataItem.Link}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default ChampionshipSponsorsPageView;
